import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { SchoolPublic, SchoolSearchResult } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  DataTable, applyClientListParams,
  type DataTableColumn, type DataTableFetchParams,
} from "@/components/data-table";
import { Plus, Pencil, Trash2, Building2, Globe2 } from "lucide-react";
import { usePinFilters } from "@/lib/pins-context";
import { PinFilterBar } from "@/components/pin-filter-bar";
import { PinRowButton } from "@/components/pin-row-button";
import { FavoriteSchoolButton } from "@/components/favorite-school-button";
import { MultiSelectDropdown } from "@/components/multi-select-dropdown";
import { PreviewPane, PreviewField, PreviewPaneToggle } from "@/components/preview-pane";
import { SchoolCrest } from "@/components/school-crest";
import { useAuth } from "@/lib/auth-context";
import { useGridPreferences } from "@/lib/use-grid-preferences";

type ViewMode = "affiliated" | "directory";

const SCHOOL_TYPE_OPTIONS = [
  { value: "P", label: "Primary" },
  { value: "I", label: "Intermediate" },
  { value: "S", label: "Secondary" },
  { value: "C", label: "Combined" },
];

const VIEW_TABS: { value: ViewMode; label: string; icon: typeof Building2 }[] = [
  { value: "affiliated", label: "Affiliated", icon: Building2 },
  { value: "directory", label: "All Schools", icon: Globe2 },
];

const PROVINCE_OPTIONS = [
  { value: "EC", label: "Eastern Cape" }, { value: "FS", label: "Free State" },
  { value: "GT", label: "Gauteng" }, { value: "KZN", label: "KwaZulu-Natal" },
  { value: "LP", label: "Limpopo" }, { value: "MP", label: "Mpumalanga" },
  { value: "NC", label: "Northern Cape" }, { value: "NW", label: "North West" },
  { value: "WC", label: "Western Cape" },
];
const PHASE_OPTIONS = [
  { value: "PRIMARY SCHOOL", label: "Primary" }, { value: "SECONDARY SCHOOL", label: "Secondary" },
  { value: "COMBINED SCHOOL", label: "Combined" }, { value: "INTERMEDIATE SCHOOL", label: "Intermediate" },
];
const SECTOR_OPTIONS = [{ value: "PUBLIC", label: "Public" }, { value: "INDEPENDENT", label: "Independent" }];

export default function SchoolsPage() {
  const { user: authUser } = useAuth();
  const [selected, setSelected] = useState<SchoolPublic | null>(null);
  const [pinned, setPinned] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [tableState, setTableState] = useState<"loading" | "error" | "empty" | "ready">("loading");
  const { activeOrgIds, activeSchoolIds } = usePinFilters();
  const hasPinFilter = activeOrgIds.length > 0 || activeSchoolIds.length > 0;

  const [searchParams, setSearchParams] = useSearchParams();
  useGridPreferences("schools");
  const viewMode = (searchParams.get("view") ?? "affiliated") as ViewMode;
  function setViewMode(v: ViewMode) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("view", v);
      for (const k of ["q", "page", "sort", "order", "province", "phase", "sector", "affiliated", "type"]) next.delete(k);
      return next;
    }, { replace: true });
  }

  const isSuperAdmin = authUser?.roles.some((r) => r.role_type === "super_admin") ?? false;
  const isOrgAdmin = isSuperAdmin || (authUser?.roles.some((r) => r.role_type === "org_admin") ?? false);
  const userOrgIds = useMemo(() => {
    if (!authUser) return [];
    return [...new Set(authUser.roles.filter((r) => r.role_type === "org_admin").map((r) => r.scope_id))];
  }, [authUser]);
  const userOrgId = userOrgIds[0] ?? null;

  const [orgs, setOrgs] = useState<{ id: string; name: string }[]>([]);
  const needsOrgFilter = isSuperAdmin || userOrgIds.length > 1;
  useEffect(() => {
    if (needsOrgFilter) {
      api.getOrganizations().then((res) => setOrgs(res.organizations.map((o) => ({ id: o.id, name: o.name })))).catch(() => {});
    }
  }, [needsOrgFilter]);

  const selectedOrgId = searchParams.get("org") ?? (needsOrgFilter ? null : userOrgId);
  const effectiveOrgId = selectedOrgId ?? userOrgId;

  // ── URL-persisted facet state ──
  const activeTypes = useMemo(() => {
    const param = searchParams.get("type");
    return param ? new Set(param.split(",")) : new Set<string>();
  }, [searchParams]);
  const dirProvinces = useMemo(() => new Set((searchParams.get("province") ?? "").split(",").filter(Boolean)), [searchParams]);
  const dirPhases = useMemo(() => new Set((searchParams.get("phase") ?? "").split(",").filter(Boolean)), [searchParams]);
  const dirSectors = useMemo(() => new Set((searchParams.get("sector") ?? "").split(",").filter(Boolean)), [searchParams]);
  const dirAffiliated = searchParams.get("affiliated");

  function updateParams(updates: Record<string, string | null>) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === "") next.delete(key);
        else next.set(key, value);
      }
      return next;
    }, { replace: true });
  }
  function toggleType(typeId: string) {
    const next = new Set(activeTypes);
    if (next.has(typeId)) next.delete(typeId); else next.add(typeId);
    updateParams({ type: next.size > 0 ? [...next].join(",") : null, page: null });
  }
  function toggleDirFilter(key: string, value: string) {
    const current = new Set((searchParams.get(key) ?? "").split(",").filter(Boolean));
    if (current.has(value)) current.delete(value); else current.add(value);
    updateParams({ [key]: current.size > 0 ? [...current].join(",") : null, page: null });
  }

  async function handleDelete(schoolId: string) {
    if (!confirm("Delete this school? This action cannot be undone.")) return;
    try {
      await api.deleteSchool(schoolId);
      if (selected?.id === schoolId) setSelected(null);
      setRefreshTick((t) => t + 1);
    } catch (e) {
      alert(e instanceof api.ApiError ? e.message : "Failed to delete school");
    }
  }

  async function affiliateSchool(ns: SchoolSearchResult) {
    if (!effectiveOrgId) return;
    try {
      await api.affiliateSchool(ns.id, effectiveOrgId);
      setRefreshTick((t) => t + 1);
    } catch (e) {
      alert(e instanceof api.ApiError ? e.message : "Failed to affiliate school");
    }
  }

  // ── Affiliated: fetch all, apply pin + type filters, then search/sort/paginate ──
  const typesCsv = [...activeTypes].join(",");
  const pinKey = `${activeOrgIds.join(",")}|${activeSchoolIds.join(",")}`;
  const affiliatedFetcher = async (params: DataTableFetchParams): Promise<{ data: SchoolPublic[]; total: number }> => {
    const res = await api.getSchools();
    let list = hasPinFilter ? res.schools.filter((s) => activeSchoolIds.includes(s.id)) : res.schools;
    if (activeTypes.size > 0) list = list.filter((s) => s.school_type_id && activeTypes.has(s.school_type_id));
    return applyClientListParams(list, params, {
      searchFields: (s) => [s.name, s.abbreviation, s.website_url, s.location],
      sortAccessors: {
        name: (s) => s.name,
        school_type: (s) => s.school_type?.name ?? "",
        location: (s) => s.location ?? "",
      },
      defaultSort: "name",
    });
  };

  // ── Directory: the searchSchools endpoint is already server-side ──
  const dirKey = `${[...dirProvinces].join(",")}|${[...dirPhases].join(",")}|${[...dirSectors].join(",")}|${dirAffiliated ?? ""}|${selectedOrgId ?? ""}`;
  const directoryFetcher = async (params: DataTableFetchParams): Promise<{ data: SchoolSearchResult[]; total: number }> => {
    const res = await api.searchSchools(params.search?.trim() ?? "", {
      province: dirProvinces.size > 0 ? [...dirProvinces].join(",") : undefined,
      phase: dirPhases.size > 0 ? [...dirPhases].join(",") : undefined,
      sector: dirSectors.size > 0 ? [...dirSectors].join(",") : undefined,
      affiliated: dirAffiliated ?? undefined,
      organization_id: selectedOrgId ?? undefined,
      sort: params.sort ?? "name",
      dir: params.order,
      page: params.page,
      limit: params.per_page,
    });
    return { data: res.schools, total: res.total };
  };

  const affiliatedColumns: DataTableColumn<SchoolPublic>[] = [
    {
      key: "name", header: "Name", sortable: true, className: "font-medium",
      render: (school) => (
        <div className="flex items-center gap-2">
          <SchoolCrest name={school.name} logoUrl={school.logo_url} primaryColor={school.primary_color} size={28} />
          <div className="min-w-0">
            <Link to={`/schools/${school.slug}`} className="hover:underline" onClick={(e) => e.stopPropagation()}>{school.name}</Link>
            {school.emis_number && <span className="ml-1.5 text-[10px] text-muted-foreground">EMIS {school.emis_number}</span>}
          </div>
        </div>
      ),
    },
    { key: "school_type", header: "Type", sortable: true, className: "text-muted-foreground", render: (s) => s.school_type?.name ?? "--" },
    { key: "location", header: "Location", sortable: true, className: "text-muted-foreground", render: (s) => s.location ?? "--" },
    {
      key: "actions", header: "", headerClassName: "w-16",
      render: (school) => (
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          <FavoriteSchoolButton schoolId={school.id} schoolName={school.name} variant="icon" />
          <PinRowButton pinType="school" entityType="School" entityId={school.id} />
          {school._actions.includes("edit") && (
            <Link to={`/schools/${school.slug}/edit`} className={buttonVariants({ variant: "ghost", size: "sm" })}><Pencil className="h-4 w-4" /></Link>
          )}
          {school._actions.includes("delete") && (
            <Button variant="ghost" size="sm" onClick={() => handleDelete(school.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
          )}
        </div>
      ),
    },
  ];

  const directoryColumns: DataTableColumn<SchoolSearchResult>[] = [
    {
      key: "name", header: "Name", sortable: true, className: "font-medium",
      render: (ns) => (
        <div className="flex items-center gap-2">
          <SchoolCrest name={ns.display_name ?? ns.name} logoUrl={ns.logo_url} primaryColor={ns.primary_color} size={28} />
          <div className="min-w-0">
            <Link to={`/schools/${ns.slug}`} className="hover:underline">{ns.display_name ?? ns.name}</Link>
            {ns.emis_number && <span className="ml-1.5 text-[10px] text-muted-foreground">EMIS {ns.emis_number}</span>}
          </div>
        </div>
      ),
    },
    { key: "province", header: "Province", sortable: true, className: "text-muted-foreground", render: (ns) => ns.province ?? "--" },
    { key: "town", header: "Town", sortable: true, className: "text-muted-foreground", render: (ns) => ns.town ?? "--" },
    { key: "sector", header: "Sector", sortable: true, className: "text-muted-foreground", render: (ns) => ns.sector ?? "--" },
    { key: "phase", header: "Phase", sortable: true, className: "text-muted-foreground", render: (ns) => ns.phase ?? "--" },
    {
      key: "actions", header: "", headerClassName: "w-24",
      render: (ns) =>
        ns.is_affiliated ? (
          <span className="rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">Affiliated</span>
        ) : isOrgAdmin ? (
          <Button variant="outline" size="sm" onClick={() => affiliateSchool(ns)}>Affiliate</Button>
        ) : null,
    },
  ];

  return (
    <div className="flex h-full gap-0" data-page="schools-list" data-state={tableState}>
      <div className="min-w-0 flex-1 flex flex-col gap-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Schools</h1>
            <p className="text-muted-foreground">
              {viewMode === "affiliated" ? "Manage schools in your organization" : "Search 25,000+ South African schools"}
            </p>
          </div>
          <div className="flex gap-2">
            {viewMode === "affiliated" && (
              <Link to="/schools/new" className={buttonVariants()} data-action="create">
                <Plus className="mr-2 h-4 w-4" /> Add School
              </Link>
            )}
            <PreviewPaneToggle pinned={pinned} onTogglePin={() => setPinned((p) => !p)} />
          </div>
        </div>

        {/* View tabs */}
        <div className="flex gap-1 border-b">
          {VIEW_TABS.map((tab) => (
            <button key={tab.value} type="button" onClick={() => setViewMode(tab.value)}
              className={`inline-flex items-center gap-1.5 border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                viewMode === tab.value ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}>
              <tab.icon className="h-4 w-4" /> {tab.label}
            </button>
          ))}
        </div>

        {viewMode === "affiliated" && <PinFilterBar entityType="Organization" />}
        {viewMode === "affiliated" && <PinFilterBar entityType="School" />}

        <div data-region="results">
          {viewMode === "affiliated" ? (
            <DataTable
              columns={affiliatedColumns}
              fetcher={affiliatedFetcher}
              fetchKey={`affiliated|${pinKey}|${typesCsv}|${refreshTick}`}
              rowKey={(s) => s.id}
              rowProps={(s) => ({ "data-item": "school", "data-item-id": s.id })}
              rowClassName={(s) => (selected?.id === s.id ? "bg-muted" : undefined)}
              searchPlaceholder="Search schools..."
              defaultSort="name"
              emptyMessage="No schools yet."
              onRowClick={(s) => setSelected(s)}
              onStateChange={setTableState}
              toolbarExtra={
                <MultiSelectDropdown label="School Type" options={SCHOOL_TYPE_OPTIONS} selected={activeTypes}
                  onToggle={toggleType} onClear={() => updateParams({ type: null, page: null })} data-field="school-type" />
              }
            />
          ) : (
            <DataTable
              columns={directoryColumns}
              fetcher={directoryFetcher}
              fetchKey={`directory|${dirKey}`}
              rowKey={(ns) => ns.id}
              rowProps={(ns) => ({ "data-item": "school", "data-item-id": ns.id })}
              searchPlaceholder="Search schools..."
              defaultSort="name"
              emptyMessage="No schools found."
              onStateChange={setTableState}
              toolbarExtra={
                <>
                  <MultiSelectDropdown label="Province" options={PROVINCE_OPTIONS} selected={dirProvinces}
                    onToggle={(v) => toggleDirFilter("province", v)} onClear={() => updateParams({ province: null, page: null })} />
                  <MultiSelectDropdown label="Phase" options={PHASE_OPTIONS} selected={dirPhases}
                    onToggle={(v) => toggleDirFilter("phase", v)} onClear={() => updateParams({ phase: null, page: null })} />
                  <MultiSelectDropdown label="Sector" options={SECTOR_OPTIONS} selected={dirSectors}
                    onToggle={(v) => toggleDirFilter("sector", v)} onClear={() => updateParams({ sector: null, page: null })} />
                  <MultiSelectDropdown label="Affiliated"
                    options={[{ value: "true", label: "Affiliated" }, { value: "false", label: "Not Affiliated" }]}
                    selected={dirAffiliated ? new Set([dirAffiliated]) : new Set<string>()}
                    onToggle={(v) => updateParams({ affiliated: dirAffiliated === v ? null : v, page: null })}
                    onClear={() => updateParams({ affiliated: null, page: null })} />
                  {needsOrgFilter && orgs.length > 0 && (
                    <select title="Organization" aria-label="Filter by organization" value={selectedOrgId ?? ""}
                      onChange={(e) => updateParams({ org: e.target.value || null, page: null })}
                      className="rounded-md border border-input bg-background px-2 py-1.5 text-sm">
                      <option value="">All Organizations</option>
                      {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                  )}
                </>
              }
            />
          )}
        </div>
      </div>
      <PreviewPane pinned={pinned} onTogglePin={() => setPinned((p) => !p)} title="School">
        {selected && (
          <>
            <PreviewField label="Name">{selected.name}</PreviewField>
            <PreviewField label="Type">{selected.school_type?.name ?? "--"}</PreviewField>
            <PreviewField label="Location">{selected.location ?? "--"}</PreviewField>
            {selected.emis_number && <PreviewField label="EMIS">{selected.emis_number}</PreviewField>}
            <PreviewField label="Slug">{selected.slug}</PreviewField>
          </>
        )}
      </PreviewPane>
    </div>
  );
}
